import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, AlertTriangleIcon, CheckIcon, HelpCircleIcon } from 'lucide-react';
import { CarliReport, FactorStrength } from '../utils/carliRecoveryReport';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const strengthTone: Record<FactorStrength, string> = {
  Strong: 'bg-positive/12 text-positive',
  Positive: 'bg-positive/12 text-positive',
  Moderate: 'bg-[#BB5E40]/12 text-[#94452E]',
  Limited: 'bg-ink/8 text-ink/55',
  Weak: 'bg-ink/8 text-ink/55'
};

// Renders the report in the format Carli is trained to deliver: estimated
// revenue generation, outlook, factors, data integrity, model confidence,
// summary, risks, and conclusion.
export function CarliReportSections({
  report
}: {
  report: CarliReport;
}) {
  return <>
      <section className="mt-5 rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-labelledby="revenue-generation-heading" data-id="element-553" data-mp="2sh11iocvfgps">
        <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-554" data-mp="3sjymfvybea7e">
          <h2 id="revenue-generation-heading" className="text-sm font-semibold" data-id="element-555" data-mp="3nldltvf5hoif">Estimated revenue generation</h2>
          <span className="rounded-full bg-[#BB5E40]/12 px-2.5 py-1 text-[11px] font-semibold text-[#94452E]" data-id="element-556" data-mp="575s3bijg9nf">
            Confidence · {report.headline.confidence}
          </span>
        </div>
        <p className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-4xl" data-id="element-557" data-mp="f5br9lcyn1ol">
          {currency(report.headline.low)} – {currency(report.headline.high)}
        </p>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink/65" data-id="element-558" data-mp="a8v00iags77a">{report.headline.statement}</p>

        <div className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-559" data-mp="k4omouhwdh2s">
          {report.outlook.map(outlook => {
          const expected = outlook.label.toLowerCase() === 'expected';
          return <div key={outlook.label} className={`rounded-xl border p-4 ${expected ? 'border-[#BB5E40] bg-[#BB5E40]/[0.06]' : 'border-ink/10 bg-cream/45'}`} data-id="element-560" data-mp="2lw1fil6lbyih">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-561" data-mp="tu8hlm5h6nlx">{outlook.label}</p>
                <p className={`mt-2 text-xl font-semibold tabular-nums ${expected ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-562" data-mp="yt1v3ekriem3">
                  {currency(outlook.value)}
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-ink/55" data-id="element-563" data-mp="17u9kt897q74k">{outlook.note}</p>
              </div>;
        })}
        </div>
        <p className="mt-4 border-t border-ink/10 pt-4 text-xs text-ink/55" data-id="element-564" data-mp="1cs1i6hsmmlg5">
          Predicted AUR <span className="font-semibold tabular-nums text-ink" data-id="element-565" data-mp="1hldx207i7dvp">{currency(report.headline.aur)}</span> per unit
        </p>
      </section>

      <section className="mt-5" aria-labelledby="factors-heading" data-id="element-566" data-mp="1ml09w8jf6s3z">
        <h2 id="factors-heading" className="text-sm font-semibold" data-id="element-567" data-mp="1rjtok4ag0p5q">Recovery factors</h2>
        <div className="mt-3 grid gap-4 lg:grid-cols-2" data-id="element-568" data-mp="1vkvmmhsmiv64">
          {report.factors.map(factor => <article key={factor.name} className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm" data-id="element-569" data-mp="3g2o1qrl7t2pc">
              <div className="flex items-start justify-between gap-3" data-id="element-570" data-mp="3byq4ynjd43zg">
                <h3 className="text-sm font-semibold" data-id="element-571" data-mp="370ewag4szzit">{factor.name}</h3>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold ${strengthTone[factor.strength] ?? strengthTone.Moderate}`} data-id="element-572" data-mp="322db9pnthq4p">
                  {factor.strength}
                </span>
              </div>
              <ul className="mt-3 space-y-2" data-id="element-573" data-mp="3uru1qhrvmwya">
                {factor.findings.map((finding, index) => <li key={index} className="flex gap-2 text-xs leading-relaxed text-ink/65" data-id="element-574" data-mp="3pyrfyvtccrsn" data-mpi={index}>
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#BB5E40]" aria-hidden="true" data-id="element-575" data-mp="3kzxyksaxogn7" />
                    {finding}
                  </li>)}
              </ul>
              <p className="mt-3 border-t border-ink/10 pt-3 text-xs font-medium leading-relaxed text-ink" data-id="element-576" data-mp="hj7p23g57lgr">{factor.takeaway}</p>
            </article>)}
        </div>
      </section>

      <div className="mt-5 grid gap-5 lg:grid-cols-2" data-id="element-577" data-mp="642zs7uwzzsi">
        <RatingCard title="Condition & historical data" rating={report.condition.rating} notes={report.condition.notes} data-id="element-578" data-mp="61hzhna9lrwv" />
        <RatingCard title="Product data integrity" rating={report.dataIntegrity.rating} notes={report.dataIntegrity.notes} data-id="element-579" data-mp="3t8an18ps9jve" />
      </div>

      <section className="mt-5 rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-labelledby="model-confidence-heading" data-id="element-580" data-mp="ku1x68enb76x">
        <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-581" data-mp="ptmwwl1n3jkp">
          <h2 id="model-confidence-heading" className="text-sm font-semibold" data-id="element-582" data-mp="b2ntrpjkpn8i">Model confidence</h2>
          <span className="rounded-full bg-[#BB5E40]/12 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-583" data-mp="g0667pwsukl9">
            {report.modelConfidence.level}
          </span>
        </div>
        <div className="mt-4 grid gap-5 sm:grid-cols-3" data-id="element-584" data-mp="18kef8c3lukb8">
          <ConfidenceColumn icon={CheckIcon} title="Known with confidence" items={report.modelConfidence.known} data-id="element-585" data-mp="2t7ozg8r0vu0l" />
          <ConfidenceColumn icon={HelpCircleIcon} title="Estimated" items={report.modelConfidence.estimated} data-id="element-586" data-mp="2o948kzxnumot" />
          <ConfidenceColumn icon={AlertTriangleIcon} title="Not yet known" items={report.modelConfidence.unknown} data-id="element-587" data-mp="3208zndmyq6nj" />
        </div>
      </section>

      <div className="mt-5 grid gap-5 lg:grid-cols-2" data-id="element-588" data-mp="2a9l3yh425th9">
        <SummaryList title="What supports recovery" icon={ArrowUpIcon} tone="positive" items={report.supports} data-id="element-589" data-mp="2aa4iroyew1r2" />
        <SummaryList title="What's holding it back" icon={ArrowDownIcon} tone="coral" items={report.holdingBack} data-id="element-590" data-mp="2jasv8f85pu1u" />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2" data-id="element-591" data-mp="3upfyyeu8r09u">
        <SummaryList title="What could lift this" icon={ArrowUpIcon} tone="positive" items={report.lifts} data-id="element-592" data-mp="3unv2uksa399v" />

        <section className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm" aria-labelledby="risks-heading" data-id="element-593" data-mp="3pspivrifs4of">
          <h2 id="risks-heading" className="text-sm font-semibold" data-id="element-594" data-mp="2sc9y1g6oft70">Primary recovery risks</h2>
          <ul className="mt-4 space-y-3" data-id="element-595" data-mp="2se4lp29c1zes">
            {report.risks.map(risk => <li key={risk.title} data-id="element-596" data-mp="2wbshe7iunw7q">
                <div className="flex items-baseline justify-between gap-3" data-id="element-597" data-mp="31dhx40h0oy25">
                  <p className="text-xs font-semibold text-ink" data-id="element-598" data-mp="24mpeav2p8rwo">{risk.title}</p>
                  <span className="shrink-0 rounded-full bg-[#BB5E40]/10 px-2 py-0.5 text-[10px] font-semibold text-[#94452E]" data-id="element-599" data-mp="24kmejjbqvker">
                    {risk.level}
                  </span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-600" data-mp="2dlawgp49e4wx">{risk.detail}</p>
              </li>)}
          </ul>
        </section>
      </div>

      <section className="mt-5 rounded-2xl border border-[#BB5E40]/25 bg-[#BB5E40]/[0.05] p-5 sm:p-6" aria-labelledby="conclusion-heading" data-id="element-601" data-mp="1kxyjn6nwhbgt">
        <h2 id="conclusion-heading" className="text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-602" data-mp="1pvgi234ak8ak">
          Recovery conclusion
        </h2>
        <p className="mt-3 font-display text-2xl font-medium tracking-tight" data-id="element-603" data-mp="1dgieyn6g5mx4">
          {currency(report.headline.low)} – {currency(report.headline.high)}
        </p>
        <p className="mt-1 text-xs text-ink/60" data-id="element-604" data-mp="14f24ygjjnmtf">
          Expected {currency(report.headline.expected)} · AUR {currency(report.headline.aur)} · Confidence {report.headline.confidence}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-ink/75" data-id="element-605" data-mp="uo7ipc6asn7z">{report.conclusion}</p>
      </section>
    </>;
}
function RatingCard({
  title,
  rating,
  notes
}: {
  title: string;
  rating: string;
  notes: string[];
}) {
  return <section className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm" data-id="element-606" data-mp="krl1qyh3egb8">
      <div className="flex items-baseline justify-between gap-3" data-id="element-607" data-mp="gp7sw171u2a8">
        <h2 className="text-sm font-semibold" data-id="element-608" data-mp="bpv7zlzqn5aa">{title}</h2>
        <span className="shrink-0 rounded-full bg-ink/6 px-2.5 py-1 text-[10px] font-semibold text-ink/60" data-id="element-609" data-mp="br6e3sgnop4k">{rating}</span>
      </div>
      <ul className="mt-3 space-y-2" data-id="element-610" data-mp="1zkqckhpw8mvi">
        {notes.map((note, index) => <li key={index} className="flex gap-2 text-xs leading-relaxed text-ink/65" data-id="element-611" data-mp="24kuu8b2jfnnb" data-mpi={index}>
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#BB5E40]" aria-hidden="true" data-id="element-612" data-mp="1qlbn9llgqicl" />
            {note}
          </li>)}
      </ul>
    </section>;
}
function ConfidenceColumn({
  icon: Icon,
  title,
  items
}: {
  icon: typeof CheckIcon;
  title: string;
  items: string[];
}) {
  return <div data-id="element-613" data-mp="1lo33jgko2698">
      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-614" data-mp="1louwejmvppvt">
        <Icon className="h-3 w-3" aria-hidden="true" data-id="element-615" data-mp="1bwynsen62hdc" />
        {title}
      </p>
      <ul className="mt-2 space-y-2" data-id="element-616" data-mp="1buw1a4l836dd">
        {items.map((item, index) => <li key={index} className="text-xs leading-relaxed text-ink/65" data-id="element-617" data-mp="xxpl8hrvvlru" data-mpi={index}>{item}</li>)}
      </ul>
    </div>;
}
function SummaryList({
  title,
  icon: Icon,
  tone,
  items
}: {
  title: string;
  icon: typeof ArrowUpIcon;
  tone: 'positive' | 'coral';
  items: {
    title: string;
    detail: string;
  }[];
}) {
  return <section className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm" data-id="element-618" data-mp="edid6qnj0vdz">
      <h2 className={`flex items-center gap-1.5 text-sm font-semibold ${tone === 'positive' ? 'text-positive' : 'text-[#94452E]'}`} data-id="element-619" data-mp="scqa1cw7vrf0">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-620" data-mp="129cvo3wdami3" />
        {title}
      </h2>
      <ul className="mt-4 space-y-3" data-id="element-621" data-mp="12705j3ggh5ty">
        {items.map(item => <li key={item.title} data-id="element-622" data-mp="x9qjlxs9zcav">
            <p className="text-xs font-semibold text-ink" data-id="element-623" data-mp="x8fdvsf9ruh4">{item.title}</p>
            <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-624" data-mp="1bycwztycdtzg">{item.detail}</p>
          </li>)}
      </ul>
    </section>;
}